"use client";

import { useCallback, useMemo } from "react";
import posthog from "posthog-js";
import { useAtom } from "jotai";
import { useSession } from "next-auth/react";
import { jwtDecode } from "jwt-decode";
import { acceptedCookiePolicyAtom } from "~/atoms";
import { CookieTypeEnum } from "~/types-and-enums/cookieTypes";
import { checkUserIsEmployee } from "~/utils/generalUtils";
import { isProductionEnv } from "~/utils/envConfig";
import { useImpersonation } from "~/hooks/useImpersonation";

type EventProperties = Record<string, string | number | boolean | null | undefined>;

type JwtClaims = {
  contact_id?: string;
  account_id?: string;
  account_name?: string;
  finished_setup?: boolean;
  user_id?: string;
};

const isPostHogReady = () => typeof window !== "undefined" && !!posthog.__loaded && !posthog.has_opted_out_capturing();

/** True when running in the deployed production environment. */
const isProd = () => isProductionEnv();

/** True when not in the deployed production environment. */
const isDev = () => !isProd();

/**
 * Hook to interact with PostHog analytics.
 *
 * PROD  → events are sent to PostHog (requires Analytics cookie consent).
 * DEV / local → events are printed to the browser console so you can verify
 *               tracking without polluting production data.
 *
 * Every event and page view is automatically enriched with user context
 * (email, name, user_type, salesforce_contact_id) from the current session.
 *
 * @example
 * const { captureEvent, identifyUser } = usePostHog();
 * captureEvent("handover_details_saved", { handover_id: "POFE-1234" });
 */
export const usePostHog = () => {
  const [acceptedCookiePolicy] = useAtom(acceptedCookiePolicyAtom);
  const { data: session } = useSession();
  const { isEmployee: isImpersonatingEmployee } = useImpersonation();
  const hasAnalyticsConsent = acceptedCookiePolicy.includes(CookieTypeEnum.Analytics);
  const isEnabled = hasAnalyticsConsent && isPostHogReady();

  const accessToken = (session?.user as { access_token?: string } | undefined)?.access_token;

  // Decode JWT for extra claims (contact_id, account_name, etc.)
  const jwtClaims = useMemo<JwtClaims>(() => {
    try {
      return accessToken ? jwtDecode<JwtClaims>(accessToken) : {};
    } catch {
      return {};
    }
  }, [accessToken]);

  /**
   * Builds a user context object that is automatically merged into every event.
   * Returns an empty object when no session is available (e.g. on auth pages).
   */
  const getUserContext = useCallback((): EventProperties => {
    if (!session?.user) return {};
    const { id, email, name } = session.user as { id?: string; email?: string; name?: string };
    // Employee when: directly logged in with @andercore.com, OR currently impersonating a supplier
    const isEmployee = isImpersonatingEmployee || checkUserIsEmployee(email || "");
    return {
      actor_user_id: id ?? null,
      actor_email: email ?? null,
      actor_name: name ?? null,
      actor_user_type: isEmployee ? "employee" : "supplier",
      actor_salesforce_contact_id: jwtClaims.contact_id ?? null,
    };
  }, [session?.user, jwtClaims, isImpersonatingEmployee]);

  /**
   * Capture a named event with optional properties.
   * User context (email, name, user_type, etc.) is automatically included.
   * Event names follow snake_case domain_action convention,
   * e.g. "handover_details_saved", "stock_offer_created".
   */
  const captureEvent = useCallback(
    (eventName: string, properties?: EventProperties) => {
      const payload = { ...getUserContext(), ...properties };
      if (isDev()) console.info("[PostHog DEV] event:", eventName, JSON.stringify(payload, null, 2));
      if (!isProd() || !isEnabled) return;
      try {
        posthog.capture(eventName, payload);
      } catch (err) {
        console.error("[PostHog] captureEvent error:", err);
      }
    },
    [isEnabled, getUserContext]
  );

  /**
   * Capture a page view for the given URL.
   * Includes page title and user context automatically.
   * Call this on SPA route changes rather than relying on autocapture.
   */
  const capturePageView = useCallback(
    (url: string) => {
      const payload = {
        $current_url: url,
        page_title: typeof document !== "undefined" ? document.title : undefined,
        page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
        ...getUserContext(),
      };
      if (isDev()) console.info("[PostHog DEV] $pageview:", url, payload);
      if (!isProd() || !isEnabled) return;
      try {
        posthog.capture("$pageview", payload);
      } catch (err) {
        console.error("[PostHog] capturePageView error:", err);
      }
    },
    [isEnabled, getUserContext]
  );

  /**
   * Identify the current user so all events are attributed correctly in PostHog.
   * Call once after successful login / session load with as many traits as available.
   */
  const identifyUser = useCallback(
    (userId: string, traits?: EventProperties) => {
      if (isDev()) console.info("[PostHog DEV] identify:", userId, traits);
      if (!isProd() || !isEnabled) return;
      try {
        posthog.identify(userId, traits);
      } catch (err) {
        console.error("[PostHog] identifyUser error:", err);
      }
    },
    [isEnabled]
  );

  /** Reset PostHog identity on logout. */
  const resetUser = useCallback(() => {
    if (isDev()) console.info("[PostHog DEV] reset user");
    if (!isProd() || !isPostHogReady()) return;
    try {
      posthog.reset();
    } catch (err) {
      console.error("[PostHog] resetUser error:", err);
    }
  }, []);

  return { captureEvent, capturePageView, identifyUser, resetUser, isEnabled };
};
